import { AVUshersSettings } from './subcomponents/AVUshersSettings';
import { AVUshersTable } from './subcomponents/AVUshersTable';
import { BsPencil, BsPlusCircle } from 'react-icons/bs';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { FailedToGetData, FloatingSaveBar, H2, Loading, RadioSelector } from 'src/components/_commons';
import { helpers, meetingServices, userServices } from 'src/services';
import { hideLoadingModalReducer, setAlertResponse, setValuesAndOpenAlertModalReducer, showLoadingModalReducer } from 'src/store';
import { millisecondsOneDay, newAttendance, newAvAssignments, newUsherAssignments } from '@monorepo/helpers';
import { typeMeeting, typeMeetingsDoc, typeUser } from '@monorepo/models';
import { useDispatch } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';
import { UserAssignmentsButton } from '../_commons/UserAssignmentsButton';

export const AdminsAVUshers = () => {
    const [isLoading1, setIsLoading1] = useState(true);
    const [isLoading2, setIsLoading2] = useState(true);
    const [meetingsDoc, setMeetingsDoc] = useState<typeMeetingsDoc | null>(null);
    const [meetingsToSave, setMeetingsToSave] = useState<typeMeeting[] | null>(null);
    const [radioValue, setRadioValue] = useState('1');
    const [refreshCounter, setRefreshCounter] = useState(0);
    const [showCrud, setShowCrud] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [users, setUsers] = useState<typeUser[] | null>(null);
    const dispatch = useDispatch();

    const sample: typeMeeting[] = useMemo(() =>
        !meetingsDoc ? [] : [...meetingsDoc.midweeks, ...meetingsDoc.publics]
            ?.filter(m =>
                radioValue === '2' || new Date(m.date).getTime() > new Date().getTime() - millisecondsOneDay
            ).sort((m1, m2) =>
                new Date(m2.date).getTime() - new Date(m1.date).getTime()
            )
        ?? []
    , [meetingsDoc, radioValue]);

    const mts: typeMeeting[] = useMemo(() =>
        meetingsDoc ?
            meetingsToSave?.filter(m =>
                ![...meetingsDoc.midweeks, ...meetingsDoc.publics].map(_m => _m.id).includes(m.id)
                || m.avAssignments.some(a => a.user?.id !== [...meetingsDoc.midweeks, ...meetingsDoc.publics].find(_m => _m.id === m.id)?.avAssignments.find(_a => _a.name === a.name)?.user?.id)
                || m.usherAssignments.some(a => a.user?.id !== [...meetingsDoc.midweeks, ...meetingsDoc.publics].find(_m => _m.id === m.id)?.usherAssignments.find(_a => _a.name === a.name)?.user?.id)
            ) || []
            :
            []
    , [meetingsDoc, meetingsToSave]);

    const meetingsToDelete: typeMeeting[] = useMemo(() =>
        meetingsDoc ?
            [...meetingsDoc.midweeks, ...meetingsDoc.publics].filter(m => !(meetingsToSave ?? []).map(_m => _m.id).includes(m.id))
            :
            []
    , [meetingsDoc, meetingsToSave]);

    const isValid: boolean = useMemo(() =>
        !!meetingsToSave
        && meetingsToSave.every(m => m.date)
        && (!!mts.length || !!meetingsToDelete.length)
    , [meetingsToDelete, meetingsToSave, mts]);

    const cancelHandler = () => {
        dispatch(setValuesAndOpenAlertModalReducer({
            title: '¿Cancelar?',
            message: 'Se van a descartar todos los cambios hechos',
            mode: 'confirm',
            animation: 3,
            execution: () => {
                setShowCrud(false);
                setMeetingsToSave([...meetingsDoc!.midweeks, ...meetingsDoc!.publics].sort((m1, m2) => new Date(m2.date).getTime() - new Date(m1.date).getTime()));
                helpers.GoToTop();
            }
        }));
    }

    const saveHandler = () => {
        if (!isValid) {
            return;
        }
        dispatch(setValuesAndOpenAlertModalReducer({
            title: 'Confirmar',
            message: 'Se van a guardar los cambios',
            mode: 'confirm',
            execution: async () => {
                dispatch(showLoadingModalReducer());
                const response = await meetingServices.SaveAvUshersAssignments(mts!, meetingsToDelete);
                dispatch(hideLoadingModalReducer());
                if (!response.success) {
                    dispatch(setAlertResponse({ response }));
                    return;
                }
                setRefreshCounter(x => x + 1);
                setShowCrud(false);
                helpers.GoToTop();
            }
        }));
    }

    useEffect(() => {
        meetingServices.Get().then(response => {
            setIsLoading1(false);
            if (!response.success || !response.data?.meetingsDoc) {
                dispatch(setAlertResponse({ response }));
                return;
            }
            setMeetingsDoc(response.data.meetingsDoc);
            const msts = [...response.data.meetingsDoc.publics, ...response.data.meetingsDoc.midweeks]
                .sort((m1, m2) => new Date(m2.date).getTime() - new Date(m1.date).getTime())
            ;
            setMeetingsToSave(msts);
        });
    }, [refreshCounter]);

    useEffect(() => {
        userServices.GetAll().then(response => {
            setIsLoading2(false);
            if (!response.success || !response.data?.users) {
                dispatch(setAlertResponse({ response }));
                return;
            }
            setUsers(response.data.users);
        });
    }, []);

    if (isLoading1 || isLoading2) {
        return <Loading mt={'80px'} />;
    }

    if (!meetingsDoc || !meetingsToSave || !users) {
        return <FailedToGetData />;
    }

    return (
        <>
        {showSettings &&
            <AVUshersSettings
                closeModal={() => setShowSettings(false)}
            />
        }
        <H2 title={'ASIGNACIONES DE AUDIO Y VIDEO Y ACOMODADORES'} />
        <UserAssignmentsButton
            meetingsDoc={meetingsDoc}
            users={users}
        />
        {!showCrud ?
            <>
            <RadioSelector
                options={[
                    { label: radioValue === '1' ? 'Viendo próximas' : 'Ver próximas', type: '1' },
                    { label: radioValue === '2' ? 'Viendo todas' : 'Ver todas', type: '2' }
                ]}
                radioValue={radioValue}
                setRadioValue={setRadioValue}
            />
            <div className={'d-flex justify-content-center gap-2 my-4'}>
                <button className={'btn btn-general-red'} style={{ width: '200px' }} onClick={() => setShowCrud(true)}>
                    <BsPencil className={'mb-1 me-2'} />Editar Asignaciones
                </button>
                <button className={'btn btn-general-red'} style={{ width: '200px' }} onClick={() => setShowSettings(true)}>
                    <FaExternalLinkAlt className={'mb-1 me-2'} />Ver Opciones
                </button>
            </div>
            </>
            :
            <div className={'d-flex justify-content-center my-4'}>
                <button className={'btn btn-general-red'}
                    onClick={() => {
                        setMeetingsToSave(x => [{
                            attendance: newAttendance,
                            avAssignments: newAvAssignments,
                            date: '',
                            groupsForCleaning: [],
                            id: 0,
                            sections: [],
                            type: null,
                            usherAssignments: newUsherAssignments,
                            weekday: 0,
                            weekNumber: 0,
                            year: 0
                        }, ...x!]);
                    }}
                >
                    <BsPlusCircle className={'mb-1 me-2'} />Agregar Reunión
                </button>
            </div>
        }
        <AVUshersTable
            isEdit={showCrud}
            meetings={showCrud ? meetingsToSave : sample}
            users={users}
            setMeetingsToSave={setMeetingsToSave}
        />
        {showCrud &&
            <FloatingSaveBar
                cancelHandler={cancelHandler}
                isDisabledSaveButton={!mts.length && !meetingsToDelete.length}
                saveHandler={saveHandler}
            />
        }
        </>
    );
}
